import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Library, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import {
  planContentLabel,
  useArchivePlanLibraryPlan,
  useCreatePlanLibraryPlan,
  useEnsurePlanTemplateIncident,
  usePlanLibraryPlans,
  useUpdatePlanLibraryPlan,
  type PlanLibraryForm,
  type PlanLibraryPlan,
  type PlanLibraryTask,
  type PlanLibraryWritePayload,
} from 'hooks/planLibrary/planLibraryHooks';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  PlanDocumentsSection,
  PlanHwcgTagsSection,
  PlanIncidentTagsSection,
  PlanLayersSection,
  PlanRosterTemplatesSection,
} from './PlanContentsSections';
import { SaveableField } from 'components/IAP/planningView/widgets/SaveableField';
import { registerHwcgTourApi } from 'components/IncidentConfig/hwcgTour/hwcgTourBridge';
import { registerFocPlanningTourApi } from 'components/MapPage/focPlanningTour/focPlanningTourBridge';
import { RuleStepNav } from '../EventsTab/RuleStep';
import { isUscgFocPlanId } from './uscgFocPlanning';
import { PlanActionDrawer } from './PlanActionDrawer';
import { PlanFormsDropdown } from './PlanFormsDropdown';
import { PlanFormView } from './PlanFormView';
import { PlanTaskTable } from './PlanTaskTable';
import {
  planLibraryStepList,
  planLibraryStepStates,
  type PlanLibraryStepId,
} from './planLibrarySteps';

export function PlanLibraryView() {
  const hwcg = useHwcgFlag();
  const { data: plans = [], isLoading } = usePlanLibraryPlans({
    ensureHwcg: hwcg,
  });
  const createPlan = useCreatePlanLibraryPlan();
  const updatePlan = useUpdatePlanLibraryPlan();
  const archivePlan = useArchivePlanLibraryPlan();
  const { data: aors = [] } = useAors();
  const aorNameById = useMemo(
    () => new Map(aors.map((aor) => [aor.id, aor.name])),
    [aors]
  );
  const [openPlanId, setOpenPlanId] = useState<string | null>(null);
  const [planToDelete, setPlanToDelete] = useState<PlanLibraryPlan | null>(
    null
  );
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [aorFilter, setAorFilter] = useState<string[]>([]);
  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [newPlanDraftKey, setNewPlanDraftKey] = useState(0);
  const [taskEditor, setTaskEditor] = useState<PlanLibraryTask | null>(null);
  const [addingTask, setAddingTask] = useState(false);
  const ensureTemplateIncident = useEnsurePlanTemplateIncident();
  const [openForm, setOpenForm] = useState<{
    form: PlanLibraryForm;
    templateIncidentId: string;
  } | null>(null);
  const planSteps = useMemo(() => planLibraryStepList(hwcg), [hwcg]);
  const [openStep, setOpenStep] = useState<PlanLibraryStepId>('name');

  const openPlan = useMemo(
    () => plans.find((plan) => plan.id === openPlanId) ?? null,
    [plans, openPlanId]
  );

  useEffect(() => {
    setOpenStep('name');
  }, [openPlanId]);

  useEffect(() => {
    if (!planSteps.some((step) => step.id === openStep)) {
      setOpenStep(planSteps[0]?.id ?? 'name');
    }
  }, [planSteps, openStep]);

  const stepStates = useMemo(
    () => (openPlan ? planLibraryStepStates(openPlan) : null),
    [openPlan]
  );
  const openStepIndex = planSteps.findIndex((step) => step.id === openStep);
  const nextStep =
    openStepIndex >= 0 && openStepIndex < planSteps.length - 1
      ? planSteps[openStepIndex + 1]
      : null;
  const aorText = useAorText();
  const labeledPlans = useMemo(
    () =>
      plans.map((plan) => ({
        plan,
        categories: plan.incidentCategories,
        aors: plan.aorIds.map((aorId) => aorNameById.get(aorId) ?? 'AOR'),
      })),
    [plans, aorNameById]
  );
  const categoryOptions = useMemo(
    () => choiceList(labeledPlans.map((row) => row.categories)),
    [labeledPlans]
  );
  const aorOptions = useMemo(
    () => choiceList(labeledPlans.map((row) => row.aors)),
    [labeledPlans]
  );
  const visiblePlans = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return labeledPlans.filter(({ plan, categories, aors }) => {
      if (!matchesChoices(categories, categoryFilter)) return false;
      if (!matchesChoices(aors, aorFilter)) return false;
      if (!needle) return true;
      return [plan.name, plan.description ?? '', ...categories, ...aors]
        .join('\n')
        .toLowerCase()
        .includes(needle);
    });
  }, [labeledPlans, query, categoryFilter, aorFilter]);
  const [tourOpenPending, setTourOpenPending] = useState(false);
  const planIdKey = plans.map((plan) => plan.id).join('\0');
  const [seenPlanKey, setSeenPlanKey] = useState(planIdKey);
  if (seenPlanKey !== planIdKey) {
    setSeenPlanKey(planIdKey);
    const previous = new Set(seenPlanKey ? seenPlanKey.split('\0') : []);
    const next = new Set(planIdKey ? planIdKey.split('\0') : []);
    if (openPlanId && previous.has(openPlanId) && !next.has(openPlanId)) {
      setOpenPlanId(null);
      setTourOpenPending(false);
      setAddingTask(false);
      setTaskEditor(null);
      setOpenForm(null);
    }
  }
  if (tourOpenPending && !openPlanId && plans[0]) {
    setOpenPlanId(plans[0].id);
    setTourOpenPending(false);
  }

  useEffect(() => {
    return registerHwcgTourApi({
      expandFirstPlan: () => {
        const first = plans[0];
        if (first) setOpenPlanId(first.id);
        else setTourOpenPending(true);
      },
    });
  }, [plans]);

  useEffect(() => {
    return registerFocPlanningTourApi({
      expandFixturePlan: () => undefined,
    });
  }, []);

  const closePlan = () => {
    setOpenPlanId(null);
    setTourOpenPending(false);
    setAddingTask(false);
    setTaskEditor(null);
    setOpenForm(null);
  };

  const persist = (
    plan: PlanLibraryPlan,
    next: Omit<Partial<PlanLibraryWritePayload>, 'name' | 'description'>,
    onSuccess?: () => void
  ) => {
    updatePlan.mutate(
      {
        id: plan.id,
        name: plan.name,
        description: plan.description,
        ...next,
      },
      { onSuccess }
    );
  };

  const selectPlan = (planId: string) => {
    setTourOpenPending(false);
    setOpenPlanId(planId);
    setAddingTask(false);
    setTaskEditor(null);
    setOpenForm(null);
  };

  const openPlanForm = (
    plan: PlanLibraryPlan,
    form: PlanLibraryForm,
    addToPlan: boolean
  ) => {
    if (isUscgFocPlanId(plan.id)) {
      toast.info('Forms on the demonstration plan cannot be opened.');
      return;
    }
    const show = (templateIncidentId: string) => {
      if (!addToPlan) {
        setOpenForm({ form, templateIncidentId });
        return;
      }
      persist(plan, { forms: [...(plan.forms ?? []), form] }, () =>
        setOpenForm({ form, templateIncidentId })
      );
    };
    if (plan.templateIncidentId) {
      show(plan.templateIncidentId);
      return;
    }
    ensureTemplateIncident.mutate(
      { planId: plan.id, formFamily: form.family },
      { onSuccess: show }
    );
  };

  return (
    <div
      className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-3 tw:text-foreground"
      data-tour="hwcg-plan-library"
    >
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div className="tw:text-sm tw:font-medium">Plan Library</div>
        <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
          <Input
            value={query}
            aria-label="Search plans"
            placeholder="Search plans"
            className="tw:w-56"
            data-tour="foc-plan-search"
            onChange={(event) => setQuery(event.target.value)}
          />
          <Button
            type="button"
            size="sm"
            data-tour="hwcg-plan-new"
            onClick={() => {
              setNewPlanDraftKey((current) => current + 1);
              setNewPlanOpen(true);
            }}
          >
            <Plus className="tw:size-4" />
            New Plan
          </Button>
        </div>
      </div>

      <div className="tw:min-h-0 tw:min-w-0 tw:flex-1 tw:overflow-auto tw:rounded-lg tw:border tw:border-solid tw:border-border">
        {isLoading ? (
          <div className="tw:p-3 tw:text-xs tw:text-muted-foreground">
            Loading plans…
          </div>
        ) : plans.length === 0 ? (
          <div
            className="tw:flex tw:min-h-[16rem] tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:p-6 tw:text-center tw:text-sm tw:text-muted-foreground"
            data-tour="hwcg-plan-add-task"
          >
            <Library className="tw:size-6" />
            Create a plan to start adding template tasks and ICS forms.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="tw:!h-auto tw:min-w-40 tw:align-top tw:!whitespace-normal tw:py-2">
                  <div className="tw:mb-1">Incident category</div>
                  <ColumnMultiFilter
                    label="incident category"
                    options={categoryOptions}
                    selected={categoryFilter}
                    onChange={setCategoryFilter}
                  />
                </TableHead>
                <TableHead className="tw:!h-auto tw:min-w-40 tw:align-top tw:!whitespace-normal tw:py-2">
                  <div className="tw:mb-1">{aorText('AOR')}</div>
                  <ColumnMultiFilter
                    label={aorText('AOR')}
                    options={aorOptions}
                    selected={aorFilter}
                    onChange={setAorFilter}
                  />
                </TableHead>
                <TableHead>Contents</TableHead>
                <TableHead className="tw:w-12">
                  <span className="tw:sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visiblePlans.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="tw:py-6 tw:text-center tw:text-sm tw:text-muted-foreground"
                  >
                    No plans match.
                  </TableCell>
                </TableRow>
              ) : (
                visiblePlans.map(({ plan, categories, aors }) => (
                  <TableRow
                    key={plan.id}
                    role="button"
                    tabIndex={0}
                    data-tour={
                      isUscgFocPlanId(plan.id) ? 'foc-plan-row' : undefined
                    }
                    className="tw:cursor-pointer"
                    aria-label={`Open ${plan.name}`}
                    onClick={() => selectPlan(plan.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        selectPlan(plan.id);
                      }
                    }}
                  >
                    <TableCell className="tw:max-w-xs tw:!whitespace-normal tw:font-medium">
                      {plan.name}
                    </TableCell>
                    <TableCell className="tw:max-w-xs tw:!whitespace-normal tw:text-muted-foreground">
                      {plan.description?.trim() || BLANK}
                    </TableCell>
                    <TableCell className="tw:!whitespace-normal">
                      {joinLabels(categories)}
                    </TableCell>
                    <TableCell className="tw:!whitespace-normal">
                      {joinLabels(aors)}
                    </TableCell>
                    <TableCell className="tw:text-muted-foreground">
                      {planContentLabel(plan)}
                    </TableCell>
                    <TableCell className="tw:text-right">
                      {isUscgFocPlanId(plan.id) ? null : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Delete ${plan.name}`}
                          title="Delete plan"
                          disabled={archivePlan.isPending}
                          onClick={(event) => {
                            event.stopPropagation();
                            setPlanToDelete(plan);
                          }}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          <Trash2 className="tw:size-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <FullScreenDialog
        open={Boolean(openPlan)}
        onOpenChange={(next) => {
          if (!next) closePlan();
        }}
        title={openPlan?.name ?? 'Plan'}
        description={openPlan ? planContentLabel(openPlan) : undefined}
        railClassName="tw:!flex tw:!w-80"
        rail={
          openPlan && stepStates ? (
            <nav
              aria-label="Plan steps"
              className="tw:flex tw:flex-col tw:gap-1"
            >
              {planSteps.map((step, index) => (
                <RuleStepNav
                  key={step.id}
                  index={index + 1}
                  title={step.title}
                  summary={stepStates[step.id].summary}
                  status={stepStates[step.id].status}
                  selected={openStep === step.id}
                  onSelect={() => setOpenStep(step.id)}
                />
              ))}
            </nav>
          ) : null
        }
        footer={
          openPlan ? (
            <>
              {isUscgFocPlanId(openPlan.id) ? null : (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={
                    updatePlan.isPending ||
                    archivePlan.isPending ||
                    ensureTemplateIncident.isPending
                  }
                  onClick={() => setPlanToDelete(openPlan)}
                >
                  <Trash2 className="tw:size-4" />
                  Delete plan
                </Button>
              )}
              <div className="tw:flex-1" />
              <Button type="button" variant="outline" onClick={closePlan}>
                Close
              </Button>
              {nextStep ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpenStep(nextStep.id)}
                >
                  Next
                </Button>
              ) : null}
            </>
          ) : null
        }
      >
        {openPlan ? (
          <ExpandedPlanBody
            plan={openPlan}
            openStep={openStep}
            busy={
              updatePlan.isPending ||
              archivePlan.isPending ||
              ensureTemplateIncident.isPending
            }
            onSaveMeta={(name, description) =>
              updatePlan.mutate({
                id: openPlan.id,
                name,
                description,
              })
            }
            onChangeContents={(next) => persist(openPlan, next)}
            onAddTask={() => {
              const openTaskEditor = () => {
                setTaskEditor(null);
                setAddingTask(true);
              };
              if (openPlan.templateIncidentId) {
                openTaskEditor();
                return;
              }
              ensureTemplateIncident.mutate(
                { planId: openPlan.id },
                { onSuccess: openTaskEditor }
              );
            }}
            onEditTask={(task) => {
              setAddingTask(false);
              setTaskEditor(task);
            }}
            onDeleteTask={(taskId) =>
              persist(openPlan, {
                tasks: openPlan.tasks.filter((task) => task.id !== taskId),
              })
            }
            onAddForm={(form) => openPlanForm(openPlan, form, true)}
            onEditForm={(form) => openPlanForm(openPlan, form, false)}
            onDeleteForm={(formId) =>
              persist(openPlan, {
                forms: (openPlan.forms ?? []).filter(
                  (form) => form.id !== formId
                ),
              })
            }
          />
        ) : null}
      </FullScreenDialog>
      {openPlan && (addingTask || taskEditor) ? (
        <PlanActionDrawer
          task={taskEditor}
          siblingTasks={openPlan.tasks}
          saving={updatePlan.isPending}
          onClose={() => {
            setAddingTask(false);
            setTaskEditor(null);
          }}
          onSave={(nextTask) => {
            const next = taskEditor
              ? openPlan.tasks.map((existing) =>
                  existing.id === taskEditor.id
                    ? { ...existing, ...nextTask, id: existing.id }
                    : existing
                )
              : [
                  ...openPlan.tasks,
                  {
                    ...nextTask,
                    id: crypto.randomUUID(),
                  } as PlanLibraryTask,
                ];
            persist(openPlan, { tasks: next }, () => {
              setAddingTask(false);
              setTaskEditor(null);
            });
          }}
        />
      ) : null}
      {openPlan && openForm ? (
        <PlanFormView
          key={openForm.form.id}
          planName={openPlan.name}
          form={openForm.form}
          templateIncidentId={openForm.templateIncidentId}
          onClose={() => setOpenForm(null)}
        />
      ) : null}

      <ConfirmDeleteDialog
        open={planToDelete !== null}
        title="Delete plan"
        description={
          planToDelete
            ? `Delete ${planToDelete.name}? It will no longer be available to apply to incidents. Workspaces already created from it are not affected.`
            : ''
        }
        pending={archivePlan.isPending}
        onOpenChange={(next) => {
          if (!next) setPlanToDelete(null);
        }}
        onConfirm={() => {
          if (!planToDelete) return;
          const deletingId = planToDelete.id;
          archivePlan.mutate(deletingId, {
            onSuccess: () => {
              setPlanToDelete(null);
              if (openPlanId === deletingId) closePlan();
            },
          });
        }}
      />

      <PlanEditorDialog
        key={newPlanDraftKey}
        open={newPlanOpen}
        pending={createPlan.isPending}
        onClose={() => setNewPlanOpen(false)}
        onSubmit={(values) => {
          createPlan.mutate(values, {
            onSuccess: (plan) => {
              setOpenPlanId(plan.id);
              setNewPlanOpen(false);
            },
          });
        }}
      />
    </div>
  );
}

const BLANK = '—';

function joinLabels(values: string[]): string {
  return values.length > 0 ? values.join(', ') : BLANK;
}

function choiceList(groups: string[][]): string[] {
  const values = new Set<string>();
  let hasBlank = false;
  for (const group of groups) {
    if (group.length === 0) hasBlank = true;
    for (const value of group) values.add(value);
  }
  const sorted = [...values].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' })
  );
  return hasBlank ? [...sorted, BLANK] : sorted;
}

function matchesChoices(values: string[], selected: string[]): boolean {
  if (selected.length === 0) return true;
  if (values.length === 0) return selected.includes(BLANK);
  return values.some((value) => selected.includes(value));
}

function ColumnMultiFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const summary =
    selected.length === 0
      ? 'All'
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Filter ${label}`}
            className="tw:h-7 tw:w-full tw:min-w-0 tw:justify-between tw:px-2 tw:font-normal"
          />
        }
      >
        <span className="tw:truncate tw:text-xs">{summary}</span>
        <ChevronDown className="tw:size-3 tw:shrink-0 tw:opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="dark tw:max-h-64">
        <DropdownMenuCheckboxItem
          checked={selected.length === 0}
          onCheckedChange={() => onChange([])}
        >
          All
        </DropdownMenuCheckboxItem>
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option}
            checked={selected.includes(option)}
            onCheckedChange={(checked) =>
              onChange(
                checked
                  ? [...selected, option]
                  : selected.filter((value) => value !== option)
              )
            }
          >
            {option}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ExpandedPlanBody({
  plan,
  openStep,
  busy,
  onSaveMeta,
  onChangeContents,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onAddForm,
  onEditForm,
  onDeleteForm,
}: {
  plan: PlanLibraryPlan;
  openStep: PlanLibraryStepId;
  busy: boolean;
  onSaveMeta: (name: string, description: string | null) => void;
  onChangeContents: (
    next: Omit<Partial<PlanLibraryWritePayload>, 'name' | 'description'>
  ) => void;
  onAddTask: () => void;
  onEditTask: (task: PlanLibraryTask) => void;
  onDeleteTask: (taskId: string) => void;
  onAddForm: (form: PlanLibraryForm) => void;
  onEditForm: (form: PlanLibraryForm) => void;
  onDeleteForm: (formId: string) => void;
}) {
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameFieldKey, setNameFieldKey] = useState(0);

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      {openStep === 'name' ? (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <SaveableField
            key={nameFieldKey}
            id={`plan-library-name-${plan.id}`}
            label="Plan name"
            value={plan.name}
            maxLength={200}
            disabled={busy}
            error={nameError}
            onSave={(value) => {
              const nextName = value.trim();
              if (!nextName) {
                setNameError('Plan name is required');
                setNameFieldKey((current) => current + 1);
                return;
              }
              setNameError(null);
              onSaveMeta(nextName, plan.description);
            }}
          />
          <SaveableField
            id={`plan-library-description-${plan.id}`}
            label="Description"
            value={plan.description}
            multiline
            rows={2}
            disabled={busy}
            onSave={(nextDescription) => {
              onSaveMeta(plan.name, nextDescription.trim() || null);
            }}
          />
        </div>
      ) : null}

      {openStep === 'tasks' ? (
        <>
          <div className="tw:flex tw:items-center tw:justify-between">
            <div className="tw:text-sm tw:font-medium">Template tasks</div>
            <Button
              type="button"
              size="sm"
              data-tour="hwcg-plan-add-task"
              onClick={onAddTask}
            >
              <Plus className="tw:size-4" />
              Add Task
            </Button>
          </div>
          <PlanTaskTable
            tasks={plan.tasks}
            onEdit={onEditTask}
            onDelete={onDeleteTask}
          />
        </>
      ) : null}

      {openStep === 'forms' ? (
        <>
          <div className="tw:flex tw:items-center tw:justify-between">
            <div className="tw:text-sm tw:font-medium">ICS forms</div>
            <PlanFormsDropdown onPick={onAddForm} />
          </div>
          {(plan.forms ?? []).length === 0 ? (
            <p className="tw:text-xs tw:text-muted-foreground">
              No ICS forms on this plan yet.
            </p>
          ) : (
            <ul className="tw:rounded-md tw:border tw:border-border">
              {(plan.forms ?? []).map((form) => (
                <li
                  key={form.id}
                  className="tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-2 tw:py-1.5 tw:last:border-b-0"
                >
                  <button
                    type="button"
                    className="tw:min-w-0 tw:flex-1 tw:text-left tw:text-sm"
                    onClick={() => onEditForm(form)}
                  >
                    <span className="tw:font-medium">{form.label}</span>
                    <span className="tw:ml-2 tw:text-xs tw:text-muted-foreground">
                      {form.family === 'fema' ? 'FEMA' : 'USCG'}
                    </span>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => onDeleteForm(form.id)}
                  >
                    <Trash2 className="tw:size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}

      {openStep === 'documents' ? (
        <PlanDocumentsSection
          plan={plan}
          busy={busy}
          onChange={(files) => onChangeContents({ files })}
        />
      ) : null}

      {openStep === 'roster' ? (
        <PlanRosterTemplatesSection
          plan={plan}
          busy={busy}
          onChange={(next) => onChangeContents({ ...next })}
        />
      ) : null}

      {openStep === 'hwcg' ? (
        <PlanHwcgTagsSection
          plan={plan}
          busy={busy}
          onChange={(tags) => onChangeContents(tags)}
        />
      ) : null}

      {openStep === 'layers' ? (
        <PlanLayersSection
          plan={plan}
          busy={busy}
          onChange={(layers) => onChangeContents({ layers })}
        />
      ) : null}

      {openStep === 'incidentTags' ? (
        <PlanIncidentTagsSection
          plan={plan}
          busy={busy}
          onChange={(tags) => onChangeContents(tags)}
        />
      ) : null}
    </div>
  );
}

function PlanEditorDialog({
  open,
  pending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onSubmit: (values: { name: string; description: string | null }) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="dark tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New plan</DialogTitle>
          <DialogDescription>
            Plans are saved to this organization and can be applied from any
            workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="tw:flex tw:flex-col tw:gap-2">
          <Label htmlFor="new-plan-name">Name</Label>
          <Input
            id="new-plan-name"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
          />
          <Label htmlFor="new-plan-description">Description</Label>
          <Textarea
            id="new-plan-description"
            value={description}
            rows={3}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!name.trim() || pending}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                description: description.trim() || null,
              })
            }
          >
            Create plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
